import React, { useCallback, useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { useSearchParams } from "react-router-dom";
import { functions } from "./firebase.js";
import { toast } from "react-toastify";
import {
  CARRIER_OPTIONS,
  DEFAULT_CARRIER_META,
  RING_DEVELOPER_CONSOLE_URL,
  RING_SETUP_STEPS,
  callableErrorMessage,
  carrierMetaFor,
  formatMailboxTime,
  mailboxDevicesFromMonitoring,
} from "./mailboxUtils.js";

function DetectionReviewCard({ detection, carrierMeta, onReview, reviewingId }) {
  const [correctedCarrier, setCorrectedCarrier] = useState(detection.carrierPredicted || "unknown");
  const [hint, setHint] = useState("");
  const busy = reviewingId === detection.id;
  const meta = carrierMetaFor(detection.carrierPredicted, carrierMeta);

  return (
    <article className="mailbox-review-card">
      <div className="mailbox-review-card-top">
        <span className="mailbox-carrier-badge" style={{ background: meta.color }}>
          {detection.carrierLabel || meta.label}
        </span>
        <span className="mailbox-review-time">{formatMailboxTime(detection.occurredAt)}</span>
      </div>
      <p className="mailbox-review-detail">
        <strong>{detection.deviceName || "Camera"}</strong>
        {typeof detection.confidence === "number" ? ` · ${Math.round(detection.confidence * 100)}% confidence` : ""}
      </p>
      {detection.vehicleDescription ? <p className="mailbox-review-detail">{detection.vehicleDescription}</p> : null}
      {detection.notes ? <p className="mailbox-review-notes">{detection.notes}</p> : null}
      {Array.isArray(detection.deliverySignals) && detection.deliverySignals.length ? (
        <p className="mailbox-review-signals">Signals: {detection.deliverySignals.join(" · ")}</p>
      ) : null}
      <label className="mailbox-review-hint-label">
        Training note (optional)
        <textarea
          className="mailbox-review-hint"
          rows={2}
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          placeholder="e.g. White Subaru with amber roof light — rural USPS carrier"
        />
      </label>
      <div className="mailbox-review-actions">
        <button type="button" className="primary-button" disabled={busy} onClick={() => onReview(detection.id, "confirm", null, hint)}>Confirm</button>
        <button type="button" className="secondary-button" disabled={busy} onClick={() => onReview(detection.id, "reject", null, hint)}>Reject</button>
        <select className="mailbox-review-select" value={correctedCarrier} onChange={(e) => setCorrectedCarrier(e.target.value)} aria-label="Correct carrier">
          {CARRIER_OPTIONS.map((id) => (
            <option key={id} value={id}>{carrierMetaFor(id, carrierMeta).label}</option>
          ))}
        </select>
        <button type="button" className="secondary-button" disabled={busy} onClick={() => onReview(detection.id, "correct", correctedCarrier, hint)}>Correct carrier</button>
      </div>
    </article>
  );
}

function AdminMailboxPage({ user, isMobile }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [detections, setDetections] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [deviceRoles, setDeviceRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [reviewingId, setReviewingId] = useState(null);

  const carrierMeta = status?.carrierMeta || DEFAULT_CARRIER_META;
  const ringStatus = status?.ringStatus || "unlinked";

  const ringLinkParams = useMemo(() => {
    const nonce = searchParams.get("nonce");
    const time = searchParams.get("time");
    const ring = searchParams.get("ring");
    if (ring === "link" && nonce && time) return { nonce, time };
    return null;
  }, [searchParams]);

  const loadDetections = useCallback(async () => {
    try {
      const getMailboxDetections = httpsCallable(functions, "getMailboxDetections");
      const result = await getMailboxDetections({ limit: 25, reviewFilter: "pending" });
      setDetections(result.data?.detections || []);
    } catch (err) {
      console.error("Detections load error:", err);
    }
  }, []);

  const handleRefreshDevices = useCallback(async () => {
    try {
      const listRingMailboxDevices = httpsCallable(functions, "listRingMailboxDevices");
      const result = await listRingMailboxDevices();
      const list = result.data?.devices || [];
      setDevices(list);
      setSelectedDeviceIds((prev) => (prev.length ? prev : list.map((d) => d.deviceId)));
      return list;
    } catch (err) {
      toast.error(callableErrorMessage(err) || "Failed to refresh Ring cameras");
      return [];
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const getMailboxStatus = httpsCallable(functions, "getMailboxStatus");
      const statusRes = await getMailboxStatus();
      const statusData = statusRes.data || null;
      setStatus(statusData);
      if (statusData?.monitoring) {
        setSelectedDeviceIds(statusData.monitoring.deviceIds || []);
        setDeviceRoles(statusData.monitoring.deviceRoles || {});
        const cachedDevices = mailboxDevicesFromMonitoring(statusData.monitoring);
        if (cachedDevices.length) setDevices(cachedDevices);
      }
      if (statusData?.ringStatus === "active" && !mailboxDevicesFromMonitoring(statusData?.monitoring).length) {
        await handleRefreshDevices();
      }
      await loadDetections();
    } catch (err) {
      toast.error(err.message || "Failed to load mailbox admin settings");
    } finally {
      setLoading(false);
    }
  }, [loadDetections, handleRefreshDevices]);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  useEffect(() => {
    if (!ringLinkParams) return;
    const finishLink = async () => {
      setLinking(true);
      try {
        const finishRingMailboxLink = httpsCallable(functions, "finishRingMailboxLink");
        await finishRingMailboxLink({ nonce: ringLinkParams.nonce, time: ringLinkParams.time });
        toast.success("Ring account linked");
        setSearchParams({});
        await loadData();
        await handleRefreshDevices();
      } catch (err) {
        toast.error(err.message || "Failed to finish Ring linking");
      } finally {
        setLinking(false);
      }
    };
    finishLink();
  }, [ringLinkParams, setSearchParams, loadData, handleRefreshDevices]);

  const handleConnectRing = async () => {
    setLinking(true);
    try {
      await httpsCallable(functions, "initiateRingMailboxLink")();
      setLinkError("");
      toast.success("Ring setup started — follow the steps below.");
      await loadData();
    } catch (err) {
      const message = callableErrorMessage(err);
      setLinkError(message);
      toast.error(message);
    } finally {
      setLinking(false);
    }
  };

  const handleFinishLink = async () => {
    setLinking(true);
    setLinkError("");
    try {
      await httpsCallable(functions, "finishRingMailboxLink")({});
      toast.success("Ring account linked");
      await loadData();
      await handleRefreshDevices();
    } catch (err) {
      const message = callableErrorMessage(err);
      setLinkError(message);
      toast.error(message);
    } finally {
      setLinking(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await httpsCallable(functions, "updateMailboxConfig")({ deviceIds: selectedDeviceIds, deviceRoles, enabled: true });
      toast.success("Camera roles saved");
      await loadData();
    } catch (err) {
      toast.error(err.message || "Failed to save camera roles");
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Ring from the community mailbox?")) return;
    setSaving(true);
    try {
      await httpsCallable(functions, "disconnectRingMailbox")();
      setDevices([]);
      setSelectedDeviceIds([]);
      toast.success("Ring disconnected");
      await loadData();
    } catch (err) {
      toast.error(err.message || "Failed to disconnect Ring");
    } finally {
      setSaving(false);
    }
  };

  const handleReview = async (detectionId, action, correctedCarrier, hint) => {
    setReviewingId(detectionId);
    try {
      await httpsCallable(functions, "reviewMailboxDetection")({
        detectionId, action, correctedCarrier, hint: hint?.trim() || undefined,
      });
      toast.success(action === "reject" ? "Detection rejected" : "Saved — AI will learn from this");
      await loadData();
    } catch (err) {
      toast.error(callableErrorMessage(err));
    } finally {
      setReviewingId(null);
    }
  };

  return (
    <div className={`page-container mailbox-page ${isMobile ? "page-container-mobile" : ""}`}>
      <header className="mailbox-page-header">
        <h1 className="page-title">Mailbox Setup (Admin)</h1>
        <p className="page-subtitle">
          Connect Ring cameras, assign roles, and review AI carrier detections.
        </p>
      </header>

      {loading ? (
        <p className="mailbox-loading">Loading admin settings…</p>
      ) : (
        <>
          <section className="mailbox-admin-panel">
            <h2>Ring connection</h2>
            <p>Status: <strong>{ringStatus}</strong>{linking ? " (working…)" : ""}</p>
            {linkError ? <p className="ring-setup-error" role="alert">{linkError}</p> : null}

            {ringStatus !== "active" ? (
              <>
                <div className="mailbox-admin-actions">
                  {ringStatus === "unlinked" ? (
                    <button type="button" className="primary-button" onClick={handleConnectRing} disabled={linking}>Connect Ring</button>
                  ) : null}
                  {ringStatus === "pending" ? (
                    <button type="button" className="primary-button" onClick={handleFinishLink} disabled={linking}>Finish linking</button>
                  ) : null}
                </div>
                {ringStatus === "pending" ? (
                  <div className="ring-setup-steps">
                    <p className="ring-setup-steps-title">Next steps:</p>
                    <ol>{RING_SETUP_STEPS.map((step) => <li key={step}>{step}</li>)}</ol>
                    <p className="ring-setup-steps-note">
                      <a href={RING_DEVELOPER_CONSOLE_URL} target="_blank" rel="noopener noreferrer">Open Ring Developer Console</a>
                    </p>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="mailbox-admin-actions">
                <button type="button" className="secondary-button" onClick={handleRefreshDevices} disabled={saving}>Refresh cameras</button>
                <button type="button" className="secondary-button" onClick={handleDisconnect} disabled={saving}>Disconnect Ring</button>
              </div>
            )}
          </section>

          {ringStatus === "active" ? (
            <section className="mailbox-admin-panel">
              <h2>Camera roles</h2>
              {devices.length > 0 ? (
                <div className="mailbox-camera-config">
                  <ul className="mailbox-camera-role-hints">
                    <li><strong>Mailbox spotlight</strong> → Community mailbox (rural USPS)</li>
                    <li><strong>Front road / front sign</strong> → Front gate (UPS, FedEx, DHL)</li>
                  </ul>
                  {devices.map((device) => {
                    const isSelected = selectedDeviceIds.includes(device.deviceId);
                    const role = deviceRoles[device.deviceId] || "general";
                    return (
                      <div key={device.deviceId} className="mailbox-camera-row">
                        <label>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              setSelectedDeviceIds((prev) =>
                                e.target.checked ? [...prev, device.deviceId] : prev.filter((id) => id !== device.deviceId),
                              );
                            }}
                          />
                          {" "}{device.name || device.deviceId}
                        </label>
                        {isSelected ? (
                          <select
                            value={role}
                            onChange={(e) => setDeviceRoles((prev) => ({ ...prev, [device.deviceId]: e.target.value }))}
                          >
                            <option value="gate">Front gate — branded trucks</option>
                            <option value="mailbox">Community mailbox — rural USPS</option>
                            <option value="general">General</option>
                          </select>
                        ) : null}
                      </div>
                    );
                  })}
                  <button type="button" className="primary-button" onClick={handleSaveConfig} disabled={saving || !selectedDeviceIds.length}>
                    Save camera roles
                  </button>
                </div>
              ) : (
                <p>Tap Refresh cameras, assign roles, and save.</p>
              )}
            </section>
          ) : null}

          {detections.length ? (
            <section className="mailbox-review-panel">
              <h2>Review AI detections</h2>
              <p className="mailbox-review-intro">
                Confirm or correct carrier guesses to reduce false positives and teach the system about your rural USPS carrier.
              </p>
              <div className="mailbox-review-list">
                {detections.map((detection) => (
                  <DetectionReviewCard
                    key={detection.id}
                    detection={detection}
                    carrierMeta={carrierMeta}
                    onReview={handleReview}
                    reviewingId={reviewingId}
                  />
                ))}
              </div>
            </section>
          ) : (
            <section className="mailbox-admin-panel">
              <h2>Review AI detections</h2>
              <p>No pending detections to review.</p>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default AdminMailboxPage;